import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AtSign, CornerDownRight, Loader2, MapPin, MessageSquarePlus, Pencil, Trash2 } from 'lucide-react';
import MentionInput, { MentionText } from '@/components/admin/MentionInput';
import { isUserMentioned } from '@/lib/mentions';
import { buildThreads } from '@/lib/noteThreads';

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(date);
}

function NoteBody({ note, mentioned, isReply, replyingTo, onDelete, onEdit, deleting }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.text || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const text = draft.trim();
    if (!text || saving) return;
    try {
      setSaving(true);
      await onEdit(note, text);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="rounded-lg border border-[#D4AF37]/45 bg-white p-3">
        <MentionInput
          value={draft}
          onChange={setDraft}
          rows={3}
          disabled={saving}
          onSubmit={save}
        />
        <div className="mt-2 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={saving}
            onClick={() => {
              setEditing(false);
              setDraft(note.text || '');
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={save}
            disabled={saving || !draft.trim()}
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        isReply ? 'bg-white' : 'bg-[#F7F8FA]',
        // A note that names you gets a gold edge so it stands out in a long list.
        mentioned
          ? 'border-l-4 border-l-[#D4AF37] border-y-[#D4AF37]/30 border-r-[#D4AF37]/30 bg-[#D4AF37]/[0.06]'
          : 'border-[rgba(6,43,26,0.10)]',
      )}
    >
      <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
        {isReply && <CornerDownRight aria-hidden="true" className="size-3.5 shrink-0 text-[#062B1A]/35" />}
        <span className="text-xs font-semibold text-[#062B1A]">{note.author || 'Staff member'}</span>
        <span className="text-[11px] text-[#062B1A]/45">{formatWhen(note.createdAt)}</span>
        {note.sourceLabel && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-[#062B1A]/[0.06] px-1.5 py-px text-[10px] font-semibold text-[#062B1A]/60"
            title={`Originally written in ${note.sourceLabel}`}
          >
            <MapPin aria-hidden="true" className="size-2.5" />
            Written in {note.sourceLabel}
          </span>
        )}
        {mentioned && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[#D4AF37]/15 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-[#8A6508]">
            <AtSign aria-hidden="true" className="size-2.5" /> You
          </span>
        )}
        {isReply && replyingTo && (
          <span className="truncate text-[11px] italic text-[#062B1A]/40">
            replying to {replyingTo}
          </span>
        )}
        {note.editedAt && (
          <span className="text-[11px] italic text-[#062B1A]/35">
            {note.editedBy ? `edited by ${note.editedBy}` : 'edited'}
          </span>
        )}
        <span className="ml-auto flex shrink-0 items-center gap-0.5">
          {onEdit && (
            <button
              type="button"
              onClick={() => {
                setDraft(note.text || '');
                setEditing(true);
              }}
              aria-label="Edit note"
              className="rounded p-1 text-[#062B1A]/35 hover:bg-[#F1F3F5] hover:text-[#062B1A]"
            >
              <Pencil aria-hidden="true" className="size-3.5" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(note)}
              disabled={deleting}
              aria-label="Delete note"
              className="rounded p-1 text-[#062B1A]/35 hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 aria-hidden="true" className="size-3.5" />
            </button>
          )}
        </span>
      </div>
      <MentionText text={note.text} className="text-sm leading-relaxed text-[#062B1A]/80" />
    </div>
  );
}

export default function NotesThread({
  notes,
  currentUserId,
  isSuperAdmin = false,
  onAdd,
  onEdit,
  onDelete,
  deletingId,
  emptyText = 'No notes yet.',
}) {
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const canManage = (note) => isSuperAdmin || Boolean(currentUserId &&
    String(note.authorId || note.authorUserId || note.author_id || note.author_user_id || '') === String(currentUserId));

  const threads = useMemo(() => buildThreads(notes), [notes]);

  const submit = async (text, parentNote) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || busy) return;
    const parentId = parentNote?.id || null;
    try {
      setBusy(true);
      await onAdd(trimmed, parentId, parentNote || null);
      if (parentId) {
        setReplyDraft('');
        setReplyTo(null);
      } else {
        setDraft('');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <MentionInput
          value={draft}
          onChange={setDraft}
          disabled={busy}
          placeholder="Add an internal note... type @ to tag a teammate, student or agency"
          onSubmit={() => submit(draft, null)}
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] text-[#062B1A]/45">
            Tagged teammates get a notification. Students and agencies never see these notes.
          </p>
          <Button
            type="button"
            onClick={() => submit(draft, null)}
            disabled={busy || !draft.trim()}
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <MessageSquarePlus className="mr-2 size-4" />}
            Add note
          </Button>
        </div>
      </div>

      {threads.length === 0 ? (
        <p className="text-sm text-[#062B1A]/50">{emptyText}</p>
      ) : (
        <div className="space-y-4">
          {threads.map(({ parent, replies }) => (
            <div key={parent.id} className="space-y-2">
              <NoteBody
                note={parent}
                mentioned={isUserMentioned(parent.mentions, currentUserId)}
                onEdit={canManage(parent) ? onEdit : undefined}
                onDelete={canManage(parent) ? onDelete : undefined}
                deleting={deletingId === parent.id}
              />

              {replies.length > 0 && (
                <div className="space-y-2 border-l-2 border-[rgba(6,43,26,0.08)] pl-3 sm:pl-5">
                  {replies.map((reply) => (
                    <NoteBody
                      key={reply.id}
                      note={reply}
                      isReply
                      replyingTo={parent.author}
                      mentioned={isUserMentioned(reply.mentions, currentUserId)}
                      onEdit={canManage(reply) ? onEdit : undefined}
                      onDelete={canManage(reply) ? onDelete : undefined}
                      deleting={deletingId === reply.id}
                    />
                  ))}
                </div>
              )}

              {replyTo === parent.id ? (
                <div className="space-y-2 pl-3 sm:pl-5">
                  <MentionInput
                    value={replyDraft}
                    onChange={setReplyDraft}
                    rows={2}
                    disabled={busy}
                    placeholder={`Reply to ${parent.author || 'this note'}...`}
                    onSubmit={() => submit(replyDraft, parent)}
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setReplyTo(null);
                        setReplyDraft('');
                      }}
                      disabled={busy}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => submit(replyDraft, parent)}
                      disabled={busy || !replyDraft.trim()}
                      className="bg-[#062B1A] text-white hover:bg-[#041B10]"
                    >
                      Reply
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setReplyTo(parent.id);
                    setReplyDraft('');
                  }}
                  className="ml-3 text-xs font-semibold text-[#8A6508] hover:underline sm:ml-5"
                >
                  Reply
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
